import { ref, Ref, onMounted, onBeforeUnmount, watch, computed } from '@vue/composition-api';
import { useHoverListener, useDomListener, useBroadCastStream } from '@/common/utils';
import lottie, { AnimationItem } from '@ks/lottie-ts';
import type AnimPlayer from '@/pure-components/anim-player/index.vue';

export function useIconLottie({ iconDoms, iconJsonUrls }: {
    iconDoms: Array<Ref<HTMLDivElement | null>>;
    iconJsonUrls: Array<string>
}) {
    const animList = ref<Array<AnimationItem | null>>([]);
    function createAnim(iconDom: HTMLDivElement | null, iconUrl: string) {
        return iconDom && lottie.loadAnimation({
            container: iconDom,
            animType: 'html',
            loop: false,
            autoplay: false,
            path: iconUrl,
        });
    }
    function playAnim(index: number) {
        const curAnim = animList.value[index];
        if (curAnim) {
            curAnim.goToAndPlay(0);
        }
    }
    function pauseAnim(index: number) {
        const curAnim = animList.value[index];
        if (curAnim) {
            curAnim.goToAndStop(0);
        }
    }
    function destroyAnim(index: number) {
        const curAnim = animList.value[index];
        curAnim && curAnim.destroy();
    }
    onMounted(() => {
        animList.value = iconDoms.map((icon, index) => createAnim(icon.value, iconJsonUrls[index]));
    });
    onBeforeUnmount(() => {
        if (animList.value && animList.value.length) {
            animList.value.forEach(anim => {
                anim?.destroy();
            });
            animList.value = [];
        }
    });
    return {
        playAnim,
        pauseAnim,
        destroyAnim,
    };
}

export function useSidebarHover(playAnim: (index: number) => void, pauseAnim: (index: number) => void) {
    const commentBtn = ref<HTMLDivElement | null>(null);
    const shareBtn = ref<HTMLDivElement | null>(null);

    function handleHoverSidebarItem(item: Ref<HTMLDivElement | null>, index: number) {
        useHoverListener({
            element: item,
            enter: () => {
                playAnim(index);
            },
            leave: () => {
                pauseAnim(index);
            },
            enterDelay: 0,
            leaveDelay: 0,
        });
    }

    handleHoverSidebarItem(commentBtn, 0);
    handleHoverSidebarItem(shareBtn, 1);

    return {
        commentBtn,
        shareBtn,
    };
}

export function useGuideAnim({
    animDom,
    path,
    tagKey,
}: {
    animDom: Ref<InstanceType<typeof AnimPlayer> | null>;
    path: string;
    tagKey: string;
}) {
    const showGuideLottie = ref(false);
    const playerConfig = ref({
        videoUrl: path,
        loop: false,
    });

    function handleAnimEnded() {
        showGuideLottie.value = false;
        localStorage.setItem(tagKey, '1');
    }

    onMounted(() => {
        if (!localStorage.getItem(tagKey)) {
            showGuideLottie.value = true;
            animDom.value?.play();
        }
    });

    useBroadCastStream('key-down-trigger', data => {
        if (showGuideLottie.value) {
            handleAnimEnded();
        }
    });
    useDomListener(() => window, 'mousewheel', () => {
        if (showGuideLottie.value) {
            handleAnimEnded();
        }
    });
    return {
        showGuideLottie,
        playerConfig,
        handleAnimEnded,
    };
}

export function useFollowLottie(lottieDom: Ref<HTMLDivElement | null>, path: string, isFollowing: Ref<boolean>) {
    const anim = ref<AnimationItem | null>(null);
    function showFollowLottie() {
        if (lottieDom.value) {
            anim.value = lottie.loadAnimation({
                container: lottieDom.value,
                animType: 'html',
                loop: false,
                autoplay: true,
                path,
            });
            anim.value.addEventListener('complete', () => {
                anim.value?.destroy();
            });
        }
    }
    const followEffect = watch(() => isFollowing.value, val => {
        if (val) {
            showFollowLottie();
        }
        followEffect();
    });
    onBeforeUnmount(() => {
        if (anim.value) {
            anim.value.destroy();
        }
    });
}

export function useLikeLottie({
    lottieDom,
    liked,
    normalPath,
    likedPath,
    clickLikePath,
    cancelPath,
}: {
    lottieDom: Ref<HTMLDivElement | null>;
    liked: Ref<boolean>;
    normalPath: string;
    likedPath: string;
    clickLikePath: string;
    cancelPath: string;
}) {
    const anim = ref<AnimationItem | null>(null);
    const likeBtn = ref<HTMLDivElement | null>(null);
    const isPlaying = ref(false);
    const showUnlike = ref(false);
    const isClickAnim = ref(false);
    const curHoverPath = computed(() => (liked.value ? likedPath : normalPath));
    function loadHoverLottie() {
        if (lottieDom.value) {
            anim.value = lottie.loadAnimation({
                container: lottieDom.value,
                animType: 'html',
                loop: false,
                autoplay: false,
                path: curHoverPath.value,
            });
        }
    }
    const showLikeLottie = (isLike: boolean) => {
        if (lottieDom.value && !isPlaying.value) {
            isPlaying.value = true;
            anim.value && anim.value.destroy();
            showUnlike.value = !isLike;
            anim.value = lottie.loadAnimation({
                container: lottieDom.value,
                animType: 'html',
                loop: false,
                autoplay: true,
                path: isLike ? clickLikePath : cancelPath,
            });
            anim.value?.addEventListener('complete', () => {
                if (!isLike) {
                    anim.value?.destroy();
                    loadHoverLottie();
                }
                isClickAnim.value = true;
                isPlaying.value = false;
                showUnlike.value = false;
            });
        }
    };
    onMounted(() => {
        loadHoverLottie();
        if (liked.value) {
            anim.value?.goToAndPlay(anim.value.totalFrames + anim.value.firstFrame, true);
        }
    });
    useHoverListener({
        element: likeBtn,
        enter: () => {
            if (anim.value && !isPlaying.value && !isClickAnim.value) {
                anim.value.goToAndPlay(0);
            }
            isClickAnim.value = false;
        },
        leave: () => {
            if (liked.value && isClickAnim.value) {
                anim.value?.destroy();
                loadHoverLottie();
            }
            isClickAnim.value = false;
        },
        enterDelay: 0,
        leaveDelay: 0,
    });
    onBeforeUnmount(() => {
        if (anim.value) {
            anim.value.destroy();
        }
    });
    return {
        likeBtn,
        showUnlike,
        showLikeLottie,
    };
}

export function useLottieUrlPrefix() {
    return process.env.NODE_ENV === 'production' ? '//s2-10623.kwimgs.com/udata/pkg/cloudcdn/' : '/';
}